import Typography from "@mui/material/Typography";
import {
  MODULE_LAYOUT_LABELS,
  MODULE_TYPE_LABELS,
} from "entities/modules/constants";
import { ModuleType } from "entities/modules/types";
import { PageModuleData, PageModuleStatus } from "entities/pages/types";
import {
  StyledCell,
  StyledNameCell,
  StyledRow,
} from "shared/components/CollapsibleTable/styled";
import CustomTooltip from "shared/components/CustomTooltip";
import { Column } from "shared/components/DragNDropTable/helpers";
import DropdownList, {
  DropdownListItemProps,
} from "shared/components/DropdownList";
import FlexCenter from "shared/components/Layout/components/FlexCenter";
import { COLOR_PRIMARY_MAIN } from "shared/theme/colors";
import { ArrowForwardIcon } from "shared/theme/icons/ArrowForwardIcon";
import { StarsIcon } from "shared/theme/icons/StarsIcon";
import { formatScheduledDate } from "shared/utils/formatScheduledDate";
import { StyledActionWrapper } from "../../../../styled";
import ModuleStatusBadge from "./components/ModuleStatusBadge";
import { ColumnsProps } from "./types";

export const getPageModulesColumns = ({
  moduleDetailsDrawer,
  handleStatus,
  onDeleteClick,
  onMoveClick,
  onDuplicateClick,
  readOnly,
}: ColumnsProps): Column<PageModuleData>[] => {
  const renderActions = (module: PageModuleData) => {
    if (readOnly) {
      return (
        <StyledActionWrapper
          onClick={() => moduleDetailsDrawer.openDrawer(module)}
        >
          <ArrowForwardIcon color={COLOR_PRIMARY_MAIN} />
        </StyledActionWrapper>
      );
    }

    let items: DropdownListItemProps[] = [
      {
        title: "Move to",
        style: "normal",
        onClick: () => onMoveClick(module),
      },
      {
        title: "Duplicate to",
        style: "normal",
        onClick: () => onDuplicateClick(module),
      },
      {
        title: "Edit",
        style: "normal",
        onClick: () => moduleDetailsDrawer.openDrawer(module),
      },
      {
        title: "Delete",
        style: "remove",
        onClick: () => onDeleteClick(module),
      },
    ];

    return <DropdownList items={items} />;
  };

  return [
    {
      headerName: "Module name",
      width: "25%",
      renderCell: (item: PageModuleData) => item.name,
    },
    {
      headerName: "Type",
      width: "20%",
      headerAlign: "left",
      align: "left",
      renderCell: (item: PageModuleData) => {
        return (
          <FlexCenter gap="0.5rem">
            {MODULE_TYPE_LABELS[item.type]}{" "}
            {item.isAiGenerated ? <StarsIcon variant="colored" /> : null}
          </FlexCenter>
        );
      },
    },
    {
      headerName: "Layout",
      width: "15%",
      headerAlign: "left",
      align: "left",
      renderCell: (item: PageModuleData) =>
        item.layout ? MODULE_LAYOUT_LABELS[item.layout] : "N/A",
    },
    {
      headerName: "Start",
      width: "15%",
      headerAlign: "left",
      align: "left",
      renderCell: (item: PageModuleData) =>
        formatScheduledDate(item.scheduledStartAt),
    },
    {
      headerName: "End",
      width: "15%",
      headerAlign: "left",
      align: "left",
      renderCell: (item: PageModuleData) =>
        formatScheduledDate(item.scheduledEndAt),
    },
    {
      headerName: "Status",
      width: "20%",
      align: "center",
      headerAlign: "center",
      renderCell: (item: PageModuleData) => (
        <ModuleStatusBadge
          module={item}
          handleStatus={handleStatus}
          readOnly={readOnly}
        />
      ),
    },
    {
      headerName: "",
      width: "10%",
      align: "right",
      headerAlign: "right",
      renderCell: (item: PageModuleData) => {
        return renderActions(item);
      },
    },
  ];
};

export const getTextForDeletingPopup = (module: PageModuleData) => {
  if (module.type === ModuleType.Games && module.selectedGames?.length) {
    return `This module contain ${module.selectedGames.length} games. Are you sure you want to delete this module?`;
  }
  return "Are you sure you want to delete this module?";
};

export type Module = PageModuleData & {
  isEmpty?: boolean;
  message?: string;
};

export const scheduleColumns = [
  { key: "brandId", title: "Brand", width: "10%" },
  { key: "pageId", title: "Page", width: "10%" },
  { key: "event", title: "Event", width: "15%" },
  { key: "scheduledStartAt", title: "Start", width: "10%" },
  { key: "scheduledEndAt", title: "End", width: "10%" },
  { key: "type", title: "Content Type", width: "10%" },
  { key: "name", title: "Title", width: "15%" },
  { key: "status", title: "Status", width: "10%" },
  { key: "actions", title: "", width: "5%" },
];

export const renderModuleRow = (
  brandId: string,
  pageName: string,
  module: Module,
  moduleDetailsDrawer: any,
  onUpdateModuleStatus?: (params: {
    moduleId: string;
    status: PageModuleStatus;
  }) => void,
) => {
  if (module.isEmpty) {
    return (
      <StyledRow key={module.id ?? "empty"} style={{ maxHeight: "48px" }}>
        <StyledCell
          colSpan={scheduleColumns.length}
          width="100%"
          style={{ textAlign: "left" }}
        >
          <Typography variant="body2" color="textSecondary">
            {module.message}
          </Typography>
        </StyledCell>
      </StyledRow>
    );
  }

  const actionItems = [
    {
      title: "Edit",
      onClick: () => moduleDetailsDrawer.openDrawer(module),
    },
  ];

  return (
    <StyledRow key={module.id} style={{ maxHeight: "48px" }}>
      <StyledCell width={scheduleColumns[0].width} textTransform="capitalize">
        {brandId}
      </StyledCell>
      <StyledCell width={scheduleColumns[1].width}>{pageName}</StyledCell>
      <StyledCell width={scheduleColumns[2].width}>
        <CustomTooltip title={module.relatedEvent?.name} width="100%">
          <StyledNameCell>{module.relatedEvent?.name ?? "-"}</StyledNameCell>
        </CustomTooltip>
      </StyledCell>
      <StyledCell width={scheduleColumns[3].width}>
        {formatScheduledDate(module.scheduledStartAt)}
      </StyledCell>
      <StyledCell width={scheduleColumns[4].width}>
        {formatScheduledDate(module.scheduledEndAt)}
      </StyledCell>
      <StyledCell width={scheduleColumns[5].width}>
        {MODULE_TYPE_LABELS[module.type]}
      </StyledCell>
      <StyledCell width={scheduleColumns[6].width}>
        <CustomTooltip title={module.name} width="100%">
          <StyledNameCell>{module.name}</StyledNameCell>
        </CustomTooltip>
      </StyledCell>
      <StyledCell width={scheduleColumns[7].width}>
        <ModuleStatusBadge
          module={module}
          handleStatus={onUpdateModuleStatus}
        />
      </StyledCell>
      <StyledCell width={scheduleColumns[8].width} align="right">
        <DropdownList items={actionItems} />
      </StyledCell>
    </StyledRow>
  );
};
